/* eslint-disable max-lines */
import React from 'react';
import {
  Accordion,
  Panel,
  Section,
  SectionSubTitle,
  Typography,
} from 'common/components';
import { analyticsUtils } from 'utils/analytics-utils';
import {
  questionAccordion,
  questionList,
  questionListItem,
  questionText,
} from './question-section-styles';


export const QuestionSection: React.FC<{ elementBlock: string }> = ({ elementBlock }) => {
  /**
   * Обработчик нажатия на колапс панели
   */
  const handlePanelClick = (question: string, order: number) => (_: string, isOpen: boolean) => {
    if (!isOpen) {
      analyticsUtils.clickButtonEvent(
        elementBlock,
        `${question.slice(0, 100)} / ${order}`,
        'Часто задаваемые вопросы'
      );
    }
  };

  return (
    <Section itemScope itemType="https://schema.org/FAQPage">
      <SectionSubTitle margin="m">Часто задаваемые вопросы</SectionSubTitle>
      <Accordion className={questionAccordion}>
        <Panel
          name="rightChoose"
          onClick={handlePanelClick('Какую систему для скважины или колодца, если вода жёсткая и с железом?', 1)}
          title="Какую систему для скважины или колодца, если вода жёсткая и с железом?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long">
                Правильный выбор начинается с&nbsp;анализа воды&nbsp;&mdash; именно он&nbsp;покажет, какие примеси есть в&nbsp;вашем источнике и&nbsp;какое решение подойдёт лучше всего.{'\n'}
              </Typography>
              <Typography variant="s" size="body_long" className={questionText}>
                В&nbsp;целом ориентироваться можно так: если у&nbsp;воды есть запах, цвет или мутность, нужна механическая очистка и&nbsp;угольный фильтр;
                при повышенном железе и&nbsp;марганце устанавливают систему обезжелезивания; при жёсткости и&nbsp;накипи&nbsp;&mdash; умягчитель.
                Если проблем несколько сразу (например, железо, жёсткость и&nbsp;органика), используется комплексная система из&nbsp;нескольких фильтров, которые работают поэтапно.
              </Typography>
              <Typography variant="s" size="body_long" className={questionText}>
                Чтобы не ошибиться с выбором, закажите анализ воды в нашем онлайн-магазине или в одном из гипермаркетов Лемана ПРО.
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="filterType"
          onClick={handlePanelClick('Хватит ли фильтра под мойку или нужна система с баком?', 1)}
          title="Хватит ли фильтра под мойку или нужна система с баком?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Картриджные системы на&nbsp;3&ndash;5 ступеней под мойку подходят только для доочистки относительно чистой воды, например из&nbsp;центрального водопровода.
                Для воды из&nbsp;скважины или колодца с&nbsp;серьёзными загрязнениями они не&nbsp;рассчитаныи быстро выходят из&nbsp;строя.
                В&nbsp;таком случае лучше выбирать полноценную систему с&nbsp;управляющим клапаном и&nbsp;баком (для соли или обратной промывки)&nbsp;&mdash; она рассчитана
                на&nbsp;сложную воду и&nbsp;служит много лет.
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="performance"
          onClick={handlePanelClick('Как понять, какая производительность системы нужна?', 1)}
          title="Как понять, какая производительность системы нужна?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Производительность системы должна перекрывать максимальный расход воды в&nbsp;доме. Проще всего посчитать так: сложите расход всех точек,
                которые могут работать одновременно (душ, кран, стиральная машина и&nbsp;т.д.), и&nbsp;добавьте запас 20&ndash;30%. В&nbsp;большинстве домов на&nbsp;2&ndash;4
                человек хватает системы с&nbsp;производительностью 1,2&ndash;2,0 м&sup3;/ч (20&ndash;33&nbsp;л/мин).
              </Typography>
              <Typography variant="s" size="body_long" className={questionText}>
                Для точного расчёта лучше обратиться к&nbsp;нашим специалистам&nbsp;&mdash; мы&nbsp;поможем подобрать оптимальный вариант.
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="size"
          onClick={handlePanelClick('Сколько места занимает система и шумно ли она работает?', 1)}
          title="Сколько места занимает система и шумно ли она работает?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Система не&nbsp;занимает много места&nbsp;&mdash; примерно 1&nbsp;м &times; 0,5&nbsp;м и&nbsp;высотой до&nbsp;1,7&nbsp;м.
                Её&nbsp;лучше поставить в&nbsp;отапливаемом техническом помещении, например, котельной или подвале, рядом с&nbsp;дренажем и&nbsp;розеткой.
              </Typography>
              <Typography variant="s" size="body_long" className={questionText}>
                Во&nbsp;время обычной фильтрации система работает совсем тихо. Немного шума появляется только при &laquo;ночной
                уборке&raquo;&nbsp;&mdash; когда она промывает фильтры (это можно настроить на&nbsp;ночное время, чтобы вам ничего не&nbsp;мешало).
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="selfInstallation"
          onClick={handlePanelClick('Можно ли установить систему самому?', 1)}
          title="Можно ли установить систему самому?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Теоретически да&nbsp;&mdash; если у&nbsp;вас есть опыт в&nbsp;сантехнике и&nbsp;работе с&nbsp;оборудованием.
                Но&nbsp;наши системы обычно лучше устанавливать профессионалам. Правильная установка и&nbsp;настройка важны,
                чтобы система работала исправно и&nbsp;долго. Неправильная установка может привести к&nbsp;поломкам, которые не&nbsp;покрываются нашей гарантией.
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="schedule"
          onClick={handlePanelClick('Как часто нужно менять картридж и засыпать соль? Сколько это стоит?', 1)}
          title="Как часто нужно менять картридж и засыпать соль? Сколько это стоит?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Уход за системой совсем несложный.
              </Typography>
              <ul className={questionList}>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    Соль&nbsp;&mdash; просто досыпайте в&nbsp;бак по&nbsp;мере расхода, обычно 1&ndash;2 раза в&nbsp;месяц.
                  </Typography>
                </li>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    Фильтрующая засыпка (смола или обезжелезиватель) служит 5&ndash;7 лет и&nbsp;дольше, если система правильно регенерирует воду.
                  </Typography>
                </li>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    Картриджи для предварительной очистки меняются раз в&nbsp;1&ndash;6&nbsp;месяцев, в&nbsp;зависимости от&nbsp;качества воды.
                  </Typography>
                </li>
              </ul>
              <Typography variant="s" size="body_long" className={questionText}>
                Вы&nbsp;можете купить расходники у&nbsp;нас и&nbsp;заменить их&nbsp;сами, или вызвать нашего специалиста&nbsp;&mdash; он&nbsp;сделает всё быстро и&nbsp;с&nbsp;гарантией.
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="problem1"
          onClick={handlePanelClick('После установки умягчителя вода стала скользкой, мыло будто плохо смывается — это нормально?', 1)}
          title="После установки умягчителя вода стала скользкой, мыло будто плохо смывается — это нормально?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Да, это нормально. Мыло на&nbsp;самом деле смывается отлично, просто вода стала мягкой, и&nbsp;остаётся скользкое ощущение.
                К&nbsp;этому быстро привыкаешь, а&nbsp;кожа и&nbsp;волосы при этом становятся мягкими и&nbsp;здоровыми.
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="problem2"
          onClick={handlePanelClick('Что делать, если давление воды в доме упало после установки фильтров?', 1)}
          title="Что делать, если давление воды в доме упало после установки фильтров?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Сначала проверьте давление воды до&nbsp;установки фильтров&nbsp;&mdash; иногда причина в&nbsp;скважинном насосе или водопроводе.
              </Typography>
              <Typography variant="s" size="body_long" className={questionText}>
                Возможные причины:
              </Typography>
              <ul className={questionList}>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    загрязнились картриджи предварительной очистки&nbsp;&mdash; их&nbsp;стоит заменить;
                  </Typography>
                </li>
                <li className={questionListItem}>
                  <Typography variant="s" size="body_long">
                    cистема подобрана не&nbsp;совсем под ваш расход воды или нуждается в&nbsp;настройке клапанов.
                  </Typography>
                </li>
              </ul>
              <Typography variant="s" size="body_long" className={questionText}>
                Если после этого давление всё ещё низкое, лучше вызвать нашего специалиста&nbsp;&mdash; он&nbsp;проверит
                систему и&nbsp;при необходимости настроит или отремонтирует&nbsp;её.
              </Typography>
            </div>
          </div>
        </Panel>

        <Panel
          name="problem3"
          onClick={handlePanelClick('Система шумит и сливает воду слишком часто или постоянно — это поломка?', 1)}
          title="Система шумит и сливает воду слишком часто или постоянно — это поломка?">
          <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
            <div itemProp="text">
              <Typography variant="s" size="body_long" className={questionText}>
                Скорее всего, что-то не&nbsp;так&nbsp;&mdash; возможно, засорился управляющий клапан или есть ошибка в&nbsp;настройках таймера.
                Если система постоянно в&nbsp;режиме промывки, лучше вызвать нашего специалиста для диагностики&nbsp;&mdash; он&nbsp;всё
                проверити&nbsp;настроит систему, чтобы она работала правильно.
              </Typography>
            </div>
          </div>
        </Panel>

      </Accordion>
    </Section>
  );
};
